import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Settings, Eye, Building2, Share2, ShieldCheck, Check, ArrowRight, Phone, MessageCircle, BadgeCheck, QrCode } from 'lucide-react';
import { BsWhatsapp } from 'react-icons/bs';
import { toast } from 'sonner';
import { userIsAdmin } from '../../../Store/Reducers';
import { getImageRoot, getUploadUrl, isMobileDevice, isMobileApp, openInExternalBrowser, shareWithImage, isPremium } from '../../../Utils/Utilities';
import { generateStoreQRCardDataUrl } from '../../../Utils/QRCodeGenerator';
import WebStoreConfig from '../../Settings/WebStoreConfig/WebStoreConfig';

const WebstoreSetupBanner = () => {
  const isAdmin = useSelector(userIsAdmin);
  const agencyData = useSelector((state) => state.agencyData.data);
  const masterData = useSelector((state) => state.masterData);
  const [logoError, setLogoError] = useState(false);
  const [isGeneratingQR, setIsGeneratingQR] = useState(false);
  const [isWebStoreConfigOpen, setIsWebStoreConfigOpen] = useState(false);

  const handleSettingsClick = (e) => {
    if (e) e.stopPropagation();
    setIsWebStoreConfigOpen(true);
  };

  const handleConfigSuccess = () => {
    setIsWebStoreConfigOpen(false);
    window.dispatchEvent(new CustomEvent("config-updated"));
  };

  const companyName = agencyData?.AgencyName || agencyData?.agencyName || agencyData?.BizName || agencyData?.bizName || "Your Business";
  const rawTagline = masterData?.tagline || agencyData?.Tagline || agencyData?.tagline || "";
  const city = agencyData?.City || agencyData?.city || "";
  const state = agencyData?.State || agencyData?.state || agencyData?.StateID || agencyData?.stateID || "";
  const locationText = [city, state].filter(Boolean).join(', ');

  const getSubtext = () => {
    if (rawTagline && rawTagline.trim() !== "") {
      return rawTagline.trim();
    }
    const codeMap = { M: "Manufacturer", T: "Trader", R: "Retailer", S: "Service Provider" };
    const rawBizTypes = (masterData?.bizTypes || agencyData?.BizType || agencyData?.bizType || "").trim();
    const bizDescStr = (masterData?.sellToDesc || agencyData?.SellToDesc || agencyData?.sellToDesc || "").trim();

    if (bizDescStr) {
      return `Supplier of ${bizDescStr}${locationText ? ` based in ${locationText}` : ""}`;
    }

    const typeLabel = codeMap[rawBizTypes.toUpperCase()];
    if (typeLabel) {
      return `${typeLabel}${locationText ? ` based in ${locationText}` : ""}`;
    }

    return `Supplier of Products & Services${locationText ? ` based in ${locationText}` : ""}`;
  };

  const supplierSubtext = getSubtext();

  const rawLogo = agencyData?.Logo || agencyData?.LogoPath || agencyData?.logo;

  const getBusinessLogoUrl = () => {
    if (!rawLogo || logoError) return null;
    if (rawLogo.startsWith('http://') || rawLogo.startsWith('https://') || rawLogo.startsWith('data:') || rawLogo.startsWith('blob:')) {
      return rawLogo;
    }
    const cleanPath = rawLogo.replace(/^Uploads[/\\]/, '');
    return getUploadUrl(`Uploads/${cleanPath}`);
  };

  const logoUrl = getBusinessLogoUrl();

  const webstoreFields = [
    agencyData?.Logo || agencyData?.LogoPath || agencyData?.logo,
    masterData?.bizTypes,
    masterData?.sellToDesc,
    masterData?.segmentID,
    masterData?.tagline,
    masterData?.description,
    masterData?.locLink,
    agencyData?.Email || agencyData?.email,
    agencyData?.City || agencyData?.city,
    agencyData?.State || agencyData?.state || agencyData?.StateID || agencyData?.stateID
  ];

  const completedCount = webstoreFields.filter(field => {
    if (field === null || field === undefined) return false;
    if (typeof field === 'string') return field.trim() !== '';
    if (typeof field === 'number') return field !== 0;
    if (Array.isArray(field)) return field.length > 0;
    return !!field;
  }).length;

  const isWebstoreComplete = true; //completedCount >= 7;

  // Show "Complete Your Profile" only to Admins if profile is incomplete.
  // If profile is complete, allow all users (Admins & Non-admins) to see "Promote Your Business" to share the webstore.
  if (!isWebstoreComplete && !isAdmin) return null;

  const percentage = Math.round((completedCount / 10) * 100);
  const radius = 26;
  const strokeWidth = 6;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  const handleOpenWebstorePreview = (e) => {
    e.stopPropagation();
    const agnParam = agencyData?.AgencyID || agencyData?.agencyID || agencyData?.Mobile || agencyData?.mobile;
    const webstoreUrl = `${window.location.origin}/web/store?m=${agnParam}`;
    window.open(webstoreUrl, "_blank", "noopener,noreferrer");
  };

  const handleShareWebstore = async (e) => {
    if (e) e.stopPropagation();
    const agnParam = agencyData?.AgencyID || agencyData?.agencyID || agencyData?.Mobile || agencyData?.mobile || "";
    const companyName = agencyData?.AgencyName || agencyData?.agencyName || agencyData?.BizName || agencyData?.bizName || "Business Profile";
    const tagline = masterData?.tagline || agencyData?.Tagline || agencyData?.tagline || "";
    const webstoreUrl = `https://sahi.club/web/store?m=${agnParam}`;

    const shareTitle = companyName;
    const shareText = `*${shareTitle}* is now on *Sahi Club*!\nYou can connect with us instantly and post any inquiry on our Sahi Club web store.\n${webstoreUrl}`;

    // Prefer business logo if uploaded; fallback to high-res share card
    const imgToShare = logoUrl || getImageRoot('Images', 'SahiClub_BG.png');

    await shareWithImage({
      title: shareTitle,
      text: shareText,
      url: webstoreUrl,
      imageUrl: imgToShare,
      imageFileName: `${companyName.replace(/[^a-zA-Z0-9]/g, '_')}_profile.png`,
      successCopyMessage: "Webstore details copied to clipboard!"
    });
  };

  const handleDownloadQR = async (e) => {
    if (e) e.stopPropagation();
    const agnParam = agencyData?.AgencyID || agencyData?.agencyID || agencyData?.Mobile || agencyData?.mobile || "";
    const webstoreUrl = `https://sahi.club/web/store?m=${agnParam}`;
    const qrViewerUrl = `https://sahi.club/web/qr?m=${agnParam}`;

    // On mobile webview apps, downloads are blocked. Open the dedicated QR viewer in external browser
    if (isMobileApp()) {
      openInExternalBrowser(qrViewerUrl);
      return;
    }

    const sahiClubLogo = getImageRoot('Images', 'SahiLogo.png');
    const isPremiumUser = isPremium();

    try {
      setIsGeneratingQR(true);
      const dataUrl = await generateStoreQRCardDataUrl({
        text: webstoreUrl,
        companyName: companyName || "Your Business",
        logoUrl: logoUrl,
        sahiClubLogoUrl: sahiClubLogo,
        isPremiumUser: isPremiumUser
      });

      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = `${(companyName || 'Store').replace(/[^a-zA-Z0-9]/g, '_')}_QR_Card.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("Store QR Card downloaded.");
    } catch (err) {
      console.error("QR Card generation error:", err);
      toast.error("Failed to generate QR Card.");
    } finally {
      setIsGeneratingQR(false);
    }
  };

  if (isWebstoreComplete) {
    return (
      <>
        <div className="px-4 mb-4">
          <div
            onClick={handleShareWebstore}
            className="bg-white rounded-xl p-3 transition-all cursor-pointer select-none active:scale-[0.99] flex flex-col gap-3 relative overflow-hidden group"
          >
          {/* Subtle background glow */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-sahi-green-light/40 rounded-full blur-2xl pointer-events-none -mr-8 -mt-8" />

          {/* Header */}
          <div className="flex items-center justify-between relative z-10">
            <span className="text-text-color text-lg sm:text-xl font-semibold flex items-center gap-1.5">
              Promote Your Business
            </span>

            {/* 3 Action Icon Buttons */}
            <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
              {/* 1. Share button */}
              <button
                type="button"
                title="Share Webstore"
                onClick={handleShareWebstore}
                className="p-1.5 hover:bg-gray-100 transition-colors text-sahi-primary-color sahi-radius flex items-center justify-center"
              >
                <Share2 className="h-[18px] w-[18px]" />
              </button>

              {/* 2. QR Code button */}
              <button
                type="button"
                title="Download Store QR Code"
                onClick={handleDownloadQR}
                disabled={isGeneratingQR}
                className="p-1.5 hover:bg-gray-100 transition-colors text-sahi-primary-color sahi-radius flex items-center justify-center disabled:opacity-50"
              >
                <QrCode className="h-[18px] w-[18px]" />
              </button>

              {/* 3. Settings button (Only for Admin) */}
              {isAdmin && (
                <button
                  type="button"
                  title="Configure Webstore"
                  onClick={handleSettingsClick}
                  className="p-1.5 hover:bg-gray-100 transition-colors text-sahi-primary-color sahi-radius flex items-center justify-center"
                >
                  <Settings className="h-[18px] w-[18px]" />
                </button>
              )}
            </div>
          </div>

          <div className="relative min-h-[165px] flex flex-col justify-between overflow-hidden rounded-lg bg-gradient-to-r from-[rgba(11,93,30,0.16)] via-[rgba(11,93,30,0.08)] to-[rgba(11,93,30,0.02)] p-4 border border-[rgba(11,93,30,0.2)]">
            {/* Top Full-Width Description Text (Red Area) */}
            <div className="z-20 relative max-w-[95%]">
              <p className="text-xs sm:text-sm text-zinc-700 font-medium leading-relaxed">
                Share your webstore with clients & prospects to generate inquiries.
              </p>
            </div>

            {/* Bottom 15-degree Tilted Mini Webstore Card Mockup (Orange Area) */}
            <div className="absolute right-[-6px] bottom-[-26px] w-[295px] sm:w-[325px] max-w-[92%] z-10 transform rotate-[-8deg] group-hover:rotate-[-5deg] group-hover:scale-[1.03] transition-all duration-300 pointer-events-none">
              <div className="bg-white rounded-2xl shadow-2xl shadow-black/20 border-2 border-zinc-200/90 p-3.5 flex flex-col gap-3 overflow-hidden">
                {/* Header Row: Blue Brand Logo Box + Company Name + Verified Badge + Subtitle */}
                <div className="flex items-start gap-2.5">
                  {/* Brand Logo Container */}
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 shadow-sm overflow-hidden select-none ${logoUrl ? 'bg-white border border-zinc-200/80' : 'bg-sahi-green-light border border-sahi-green/20'}`}>
                    {logoUrl ? (
                      <img
                        src={logoUrl}
                        alt={companyName}
                        onError={() => setLogoError(true)}
                        className="w-full h-full object-cover rounded-xl aspect-square"
                      />
                    ) : (
                      <Building2 className="w-5 h-5 text-sahi-green" />
                    )}
                  </div>

                  {/* Info Column */}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <h4 className="text-[12px] font-bold text-zinc-900 leading-tight whitespace-normal break-words">
                        {companyName}
                      </h4>
                      {/* Verified Badge matching Webstore.jsx */}
                      <BadgeCheck className="h-4 w-4 text-[var(--sahi-green)] shrink-0" title="Verified Business" />
                    </div>

                    <p className="text-[9.5px] text-zinc-500 leading-snug mt-1">
                      {supplierSubtext}
                    </p>
                  </div>
                </div>

                {/* Bottom Action Icon Buttons matching reference screenshot */}
                <div className="flex items-center gap-2 pt-0.5 pb-3">
                  <div className="w-7 h-7 rounded-lg border border-zinc-200 bg-zinc-50 flex items-center justify-center text-zinc-700 shadow-2xs">
                    <Phone className="w-3.5 h-3.5" />
                  </div>
                  <div className="w-7 h-7 rounded-lg border border-zinc-200 bg-zinc-50 flex items-center justify-center text-zinc-700 shadow-2xs">
                    <BsWhatsapp className="w-3.5 h-3.5" />
                  </div>
                  <div className="w-7 h-7 rounded-lg border border-zinc-200 bg-zinc-50 flex items-center justify-center text-zinc-700 shadow-2xs">
                    <Share2 className="w-3.5 h-3.5" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <WebStoreConfig
        open={isWebStoreConfigOpen}
        onClose={() => setIsWebStoreConfigOpen(false)}
        onSuccess={handleConfigSuccess}
      />
    </>
    );
  }

  return (
    <>
      <div className="px-4 mb-4">
        <div
          onClick={handleSettingsClick}
          className="bg-white rounded-xl p-4 px-3 pt-3 transition-all cursor-pointer select-none active:scale-[0.99] flex flex-col gap-3.5"
        >
          {/* Header: Title and Action Buttons aligned on the same line */}
          <div className="flex items-center justify-between">
            <span className="text-text-color text-lg sm:text-xl font-semibold">
              Complete Your Profile
            </span>

            <div className="flex items-center gap-1 flex-shrink-0">
              <button
                onClick={handleOpenWebstorePreview}
                className="p-1.5 hover:bg-gray-100 transition-colors text-sahi-primary-color rounded-lg"
              >
                <Eye className="h-5 w-5" />
              </button>
              <button
                onClick={handleSettingsClick}
                className="p-1.5 hover:bg-gray-100 transition-colors text-sahi-primary-color rounded-lg"
              >
                <Settings className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Description & Circular Progress Side-by-Side */}
          <div className="flex items-center justify-between gap-4 -mt-1">
            {/* Left Column: Circular Progress Ring */}
            <div className="flex-shrink-0 relative w-16 h-16 flex items-center justify-center mr-1">
              <svg className="w-16 h-16 transform -rotate-90">
                <circle
                  cx="32"
                  cy="32"
                  r={radius}
                  className="stroke-[#bf57001c]"
                  strokeWidth={strokeWidth}
                  fill="transparent"
                />
                <circle
                  cx="32"
                  cy="32"
                  r={radius}
                  className="stroke-sahi-orange transition-all duration-500 ease-out"
                  strokeWidth={strokeWidth}
                  fill="transparent"
                  strokeDasharray={circumference}
                  strokeDashoffset={strokeDashoffset}
                  strokeLinecap="round"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-[12px] font-black text-zinc-800">{percentage}%</span>
              </div>
            </div>

            <div className="flex-1 space-y-3 text-left">
              <p className="text-[13px] text-zinc-500 leading-normal">
                Complete your webstore profile to showcase your business online.
              </p>
            </div>
          </div>
        </div>
      </div>
      <WebStoreConfig
        open={isWebStoreConfigOpen}
        onClose={() => setIsWebStoreConfigOpen(false)}
        onSuccess={handleConfigSuccess}
      />
    </>
  );
};

export default WebstoreSetupBanner;
